import { useCallback, useMemo } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { Box, Skeleton, useTheme } from '@/ui/primitives';
import { s } from '../../../theme/tokens';
import { DataGrid } from '../../primitives/DataGrid';
import { resolveGridColumnAlignment, wrapDataGridColumns } from './wrapDataGridColumns';
import type { SpacingToken } from '@/theme/tokens';
import type {
  DataGridProps,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridValidRowModel,
  SxProps,
  Theme,
} from '@/ui/primitives';
import type { ReactElement } from 'react';

type LoadingRow = { id: string; __loading: true };

type SkeletonDataGridProps<T extends GridValidRowModel> = {
  columns: Array<GridColDef<T>>;
  loadingRowCount?: number;
  totalRowCount?: number;
  pageSizeOptions?: number[];
  pageSize?: number;
  paginationModel?: GridPaginationModel;
  autoHeight?: boolean;
  showToolbar?: boolean;
  getRowId: (row: T) => GridRowId;
  getRowHeight?: DataGridProps['getRowHeight'];
  cellPaddingY?: SpacingToken;
  columnVisibilityModel?: DataGridProps['columnVisibilityModel'];
  slotProps?: DataGridProps['slotProps'];
  localeText?: DataGridProps['localeText'];
  sx?: DataGridProps['sx'];
  hideFooter?: boolean;
};

const resolvePadding = (token: SpacingToken): string | number => {
  switch (token) {
    case 'none':
      return 0;
    case 'xxxs':
      return s('xxs');
    case 'xxs':
    case 'xs':
      return s('xs');
    case 'sm':
      return s('sm');
    case 'md':
      return s('md');
    case 'lg':
      return s('lg');
    case 'xl':
      return s('xl');
    case '2xl':
      return s('2xl');
    case '3xl':
      return s('3xl');
    case '4xl':
      return s('4xl');
    case '5xl':
      return s('5xl');
    case 'gutter':
      return s('gutter');
    case 'section':
      return s('section');
    default:
      return s('3xl');
  }
};

export const SkeletonDataGrid = <T extends GridValidRowModel>({
  columns,
  loadingRowCount = 10,
  totalRowCount,
  pageSizeOptions = [5, 10, 25, 50],
  pageSize,
  paginationModel,
  autoHeight = false,
  showToolbar = false,
  getRowId,
  getRowHeight,
  cellPaddingY,
  columnVisibilityModel,
  slotProps,
  localeText,
  sx,
  hideFooter = false,
}: SkeletonDataGridProps<T>): ReactElement => {
  const theme = useTheme();
  const resolvedPageSize =
    pageSize && pageSizeOptions.includes(pageSize) ? pageSize : (pageSizeOptions[0] ?? 5);

  const loadingRows = useMemo<LoadingRow[]>(
    () =>
      Array.from({ length: loadingRowCount }, (_value, index) => ({
        id: `loading-${index}`,
        __loading: true,
      })),
    [loadingRowCount],
  );

  const columnsWithLoading = useMemo(() => {
    const skeletonColumns = columns.map((column) => {
      const { align, headerAlign } = resolveGridColumnAlignment(column);
      const cellJustifyContent =
        align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
      const headerJustifyContent =
        headerAlign === 'center' ? 'center' : headerAlign === 'right' ? 'flex-end' : 'flex-start';
      return {
        ...column,
        renderHeader: (params) => {
          const computedWidth = params.colDef.computedWidth;
          const widthSource =
            typeof computedWidth === 'number'
              ? computedWidth
              : typeof column.width === 'number'
                ? column.width
                : typeof column.minWidth === 'number'
                  ? column.minWidth
                  : null;
          const headerSkeletonWidth =
            typeof widthSource === 'number'
              ? Math.max(
                  componentSizes.dataGrid.columns.xs,
                  Math.min(widthSource * 0.6, componentSizes.dataGrid.columns.max),
                )
              : '60%';
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: headerJustifyContent,
                height: '100%',
                width: '100%',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Skeleton
                variant="text"
                width={headerSkeletonWidth}
                sx={{
                  height: componentSizes.dataGrid.skeletonHeight,
                  transform: 'none',
                  maxWidth: '100%',
                }}
              />
            </Box>
          );
        },
        renderCell: () => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: cellJustifyContent,
              height: '100%',
              width: '100%',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Skeleton
              variant="text"
              width="60%"
              sx={{
                height: componentSizes.dataGrid.skeletonHeight,
                transform: 'none',
                maxWidth: '100%',
              }}
            />
          </Box>
        ),
      };
    }) as Array<GridColDef<T>>;
    return wrapDataGridColumns(skeletonColumns, {
      getBaseCellSx: () => (cellPaddingY ? { py: resolvePadding(cellPaddingY) } : undefined),
    });
  }, [cellPaddingY, columns]);

  const resolveRowId = useCallback(
    (row: T) => {
      if ('__loading' in row) {
        return (row as unknown as LoadingRow).id;
      }
      return getRowId(row);
    },
    [getRowId],
  );

  const baseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    '& .MuiDataGrid-footerContainer': {
      borderTop: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
    },
  };
  const resolvedSx: SxProps<Theme> = [
    baseSx,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as SxProps<Theme>;
  const resolvedPaginationModel = paginationModel ?? { page: 0, pageSize: resolvedPageSize };
  const resolvedRowCount = typeof totalRowCount === 'number' ? totalRowCount : loadingRows.length;
  const showFooterSkeleton = !hideFooter && typeof totalRowCount !== 'number';
  const defaultSlotProps: NonNullable<DataGridProps['slotProps']> | null =
    typeof totalRowCount === 'number'
      ? null
      : {
          pagination: {
            labelDisplayedRows: () => '',
          },
        };
  const resolvedSlotProps: DataGridProps['slotProps'] = defaultSlotProps
    ? slotProps
      ? {
          ...defaultSlotProps,
          ...slotProps,
          pagination: {
            ...defaultSlotProps.pagination,
            ...(slotProps.pagination ?? {}),
          },
        }
      : defaultSlotProps
    : slotProps;

  return (
    <>
      <DataGrid
        rows={loadingRows as unknown as T[]}
        columns={columnsWithLoading}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooterSelectedRowCount
        hideFooter={hideFooter || showFooterSkeleton}
        pageSizeOptions={pageSizeOptions}
        paginationMode="server"
        paginationModel={resolvedPaginationModel}
        rowCount={resolvedRowCount}
        getRowId={resolveRowId}
        initialState={{ pagination: { paginationModel: resolvedPaginationModel } }}
        autoHeight={autoHeight}
        getRowHeight={getRowHeight}
        showToolbar={showToolbar}
        columnVisibilityModel={columnVisibilityModel}
        slotProps={resolvedSlotProps}
        localeText={localeText}
        sx={resolvedSx}
      />
      {showFooterSkeleton ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
            minHeight: theme.app.density.dataGrid.headerHeight,
            height: theme.app.density.dataGrid.headerHeight,
            paddingLeft: s('xxs'),
            paddingRight: s('none'),
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Skeleton
            variant="text"
            width={componentSizes.dataGrid.columns.md}
            sx={{ height: componentSizes.dataGrid.skeletonHeight, transform: 'none' }}
          />
        </Box>
      ) : null}
    </>
  );
};
